import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Trash2, Pencil, X, Eye, EyeOff } from "lucide-react";
import ImageUploader from "../../components/ImageUploader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const empty = { url: "", thumb_url: "", title: "", category: "general", visible_in_gallery: true };

const categories = [
  { value: "general", label: "Allgemein" },
  { value: "music", label: "Musik" },
  { value: "dance", label: "Tanz" },
  { value: "event", label: "Event" },
  { value: "workshop", label: "Workshop" },
];

const fullUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/gallery").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (it) => { setForm({ ...empty, ...it }); setEditing(it); };
  const close = () => setEditing(null);

  const onUploaded = (res) => {
    setForm(f => ({ ...f, url: res.url, thumb_url: res.thumb_url }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.url) { toast.error("Bitte ein Bild hochladen oder URL angeben"); return; }
    try {
      const payload = {
        url: form.url,
        title: form.title,
        category: form.category,
        visible_in_gallery: form.visible_in_gallery !== false,
      };
      if (editing === "new") await api.post("/admin/gallery", payload);
      else await api.put(`/admin/gallery/${editing.id}`, payload);
      toast.success("Gespeichert");
      close();
      load();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const toggleVisible = async (it) => {
    const next = it.visible_in_gallery === false;
    try {
      await api.put(`/admin/gallery/${it.id}`, {
        url: it.url,
        title: it.title || "",
        category: it.category || "general",
        visible_in_gallery: next,
      });
      toast.success(next ? "In Galerie sichtbar" : "Aus Galerie ausgeblendet");
      load();
    } catch { toast.error("Fehler beim Aktualisieren"); }
  };

  const remove = async (it) => {
    if (!window.confirm("Bild wirklich löschen?")) return;
    await api.delete(`/admin/gallery/${it.id}`);
    load();
  };

  const filtered = filter === "all" ? items : items.filter(i => i.category === filter);

  return (
    <div className="space-y-6" data-testid="admin-gallery">
      <Toaster position="top-center" theme="light" />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif-display text-3xl">Galerie</h1>
          <p className="text-sm text-muted-foreground">{items.length} Bilder</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filter} onChange={e => setFilter(e.target.value)} className="bg-input/30 border border-border rounded-md px-3 py-2 text-sm">
            <option value="all">Alle Kategorien</option>
            {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <button onClick={openNew} data-testid="gallery-new-btn" className="btn-gold rounded-full px-5 py-2.5 text-sm">+ Neu</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map(it => {
          const isHidden = it.visible_in_gallery === false;
          return (
          <div key={it.id} className={`card-academy rounded-lg overflow-hidden group relative ${isHidden ? "opacity-60" : ""}`}>
            <div className="aspect-square overflow-hidden">
              <img src={fullUrl(it.url)} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            </div>
            {isHidden && (
              <div className="absolute bottom-12 left-2 bg-destructive/90 backdrop-blur text-[10px] uppercase tracking-[0.2em] text-white px-2 py-1 rounded-md flex items-center gap-1 z-10" data-testid={`gallery-hidden-badge-${it.id}`}>
                <EyeOff className="w-3 h-3" /> Versteckt
              </div>
            )}
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
              <div className="text-xs text-white">{it.title || "—"}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-primary mt-0.5">{it.category}</div>
            </div>
            {/* Always-visible eye toggle (touch-friendly) */}
            <button
              onClick={() => toggleVisible(it)}
              title={isHidden ? "In Galerie anzeigen" : "Aus Galerie ausblenden"}
              data-testid={`gallery-toggle-visibility-${it.id}`}
              className={`absolute top-2 right-2 backdrop-blur p-2 rounded-full text-white shadow-lg transition-colors ${isHidden ? "bg-destructive/80 hover:bg-destructive" : "bg-primary/80 hover:bg-primary"}`}
            >
              {isHidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            {/* Edit / delete: always visible on touch, hover-emphasized on desktop */}
            <div className="absolute top-2 left-2 flex gap-1 opacity-90 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
              <button onClick={() => openEdit(it)} title="Bearbeiten" className="bg-black/70 backdrop-blur p-2 rounded-full text-white hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(it)} title="Löschen" className="bg-black/70 backdrop-blur p-2 rounded-full text-white hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        );})}
        {filtered.length === 0 && <div className="col-span-full text-muted-foreground text-center py-12">Keine Bilder</div>}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" role="dialog" data-testid="gallery-modal" onClick={close}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-card border border-border rounded-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="font-serif-display text-xl">{editing === "new" ? "Neues Bild" : "Bild bearbeiten"}</div>
              <button type="button" onClick={close} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {form.url && (
                <div className="aspect-video overflow-hidden rounded-md border border-border">
                  <img src={fullUrl(form.url)} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                <ImageUploader onUploaded={onUploaded} buttonLabel={form.url ? "Anderes Bild wählen" : "Bild hochladen"} testId="gallery-upload-btn" />
                <span className="text-xs text-muted-foreground">JPG, PNG, WEBP – max 10 MB</span>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Bild URL (optional, wird automatisch beim Upload gesetzt)</label>
                <input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })}
                  className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Titel</label>
                  <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                    className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Kategorie</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-primary">
                    {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <label className="flex items-center justify-between gap-3 p-3 border border-border rounded-md cursor-pointer hover:border-primary/50 transition-colors" data-testid="gallery-visibility-row">
                <div>
                  <div className="text-sm font-medium">In Galerie anzeigen</div>
                  <div className="text-xs text-muted-foreground">Aus deaktivieren, wenn das Bild z.B. nur intern oder als Profilbild benötigt wird.</div>
                </div>
                <input
                  type="checkbox"
                  checked={form.visible_in_gallery !== false}
                  onChange={(e) => setForm({ ...form, visible_in_gallery: e.target.checked })}
                  data-testid="gallery-visibility-toggle"
                  className="w-5 h-5 accent-[hsl(var(--primary))] cursor-pointer"
                />
              </label>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-3">
              <button type="button" onClick={close} className="px-5 py-2 text-sm border border-border rounded-full hover:border-muted-foreground">Abbrechen</button>
              <button type="submit" data-testid="gallery-save-btn" className="px-6 py-2 text-sm rounded-full btn-gold">Speichern</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Gallery;
